// Creating an AudioContext, on the phones clinicians actually carry.
//
// `new AudioContext({ sampleRate: 24000 })` appears ten times across the three
// views, and it is a ReferenceError on iOS Safari before 14.5 and on older
// Android browsers, which expose only `webkitAudioContext`. That does not fail
// quietly: it throws inside the handler that was setting up playback, so audio
// never arrives AND whatever else that handler was going to do never runs.
// Reported as "audio not piping through to the clinician mobile" and "mobile
// view doesn't show up well on older phones" — plausibly the same line.
//
// The sampleRate option is a second, separate problem: older WebKit ignores or
// rejects it, so a construction that survives the first hurdle can still throw
// on the argument. Hence the retry without options.
//
// Returns null rather than throwing when there is no usable constructor at all.
// Callers must handle that — a consult without audio is bad, a consult with a
// white screen is worse.
export function createAudioContext(options) {
  const Ctor = (typeof window !== 'undefined')
    && (window.AudioContext || window.webkitAudioContext);
  if (!Ctor) {
    console.warn('[audio] no AudioContext on this browser — audio disabled');
    return null;
  }
  try {
    return new Ctor(options);
  } catch (err) {
    // Older WebKit rejects the options bag. A context at the device's own rate
    // still plays; the buffers carry their own sampleRate when they are created.
    try {
      return new Ctor();
    } catch (err2) {
      console.warn('[audio] AudioContext construction failed:', err2.message);
      return null;
    }
  }
}

// Safari suspends contexts created outside a user gesture, and stays suspended
// until one arrives. Callers resume on the join tap; this keeps that harmless
// if the context is null or already running.
export function resumeAudioContext(ctx) {
  if (!ctx || ctx.state !== 'suspended') return;
  const p = ctx.resume();
  if (p && typeof p.catch === 'function') p.catch(() => { /* needs a gesture */ });
}

// The loudspeaker route, for phones.
//
// iOS sends a bare AudioContext to the EARPIECE once a microphone capture is
// live — and both the patient and the on-call doctor hold the phone in their
// hand, not against their ear. Only an <audio> element reliably reaches the
// loudspeaker, so the context is piped through a MediaStreamDestination and
// THAT is played from a hidden <audio>.
//
// Every view that plays relayed speech used to carry its own copy of this, and
// each copy had a way to go silent:
//   - arming before the <audio> was mounted cached a destination nothing played
//   - a play() refused for want of a user gesture left the route armed, so
//     every later chunk went into a dead stream: silence, not even the earpiece
//   - re-arming on every chunk after a refusal built a new destination per
//     chunk, ~20 a second, for the rest of the consult
//
// Sources connect to `output(ctx)`, a single GainNode, and the route swaps
// what THAT feeds. So a refusal that arrives after chunks are already
// scheduled loses none of them — they follow the bus to the context output.
// A refusal is sticky until `allowRetry()`, which callers invoke from the next
// user tap, the only place play() is likely to succeed.
export function createSpeakerRoute(label = 'audio') {
  let bus = null;
  let busCtx = null;
  let dest = null;
  let refused = false;

  const busFor = (ctx) => {
    if (!bus || busCtx !== ctx) {
      bus = ctx.createGain();
      busCtx = ctx;
      bus.connect(dest || ctx.destination);
    }
    return bus;
  };

  return {
    // Safe to call on every chunk: a no-op once armed, refused, or when either
    // the context or the element does not exist yet.
    arm(ctx, el) {
      if (dest || refused || !ctx || !el) return;
      const d = ctx.createMediaStreamDestination();
      const b = busFor(ctx);
      try { b.disconnect(); } catch { /* not connected */ }
      b.connect(d);
      el.srcObject = d.stream;
      dest = d;
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          // Only a real refusal counts. Assigning srcObject and calling play()
          // back to back routinely rejects with AbortError ("interrupted by a
          // new load request") while the autoplay element starts anyway; the
          // patient view relied on exactly that for years. Treating every
          // rejection as a refusal sent the patient's audio to the earpiece.
          const notAllowed = err && err.name === 'NotAllowedError';
          if (!notAllowed || !el.paused) return;
          console.warn(`[${label}] loudspeaker route refused, using context output:`, err.message);
          refused = true;
          if (dest === d) {
            dest = null;
            try { b.disconnect(); } catch { /* not connected */ }
            b.connect(ctx.destination);
          }
          try { el.srcObject = null; } catch { /* already gone */ }
        });
      }
    },
    // What a BufferSource connects to. Always the bus; never the destination
    // directly, or a late refusal would strand whatever was already scheduled.
    output(ctx) { return busFor(ctx); },
    allowRetry() { refused = false; },
    // The nodes belong to the context being closed and die with it.
    reset(el) {
      bus = null; busCtx = null; dest = null; refused = false;
      if (el) { try { el.srcObject = null; } catch { /* already gone */ } }
    }
  };
}
